<script setup>
import { ref, toRef, defineProps, defineEmits, computed, onMounted } from 'vue';
import BaseModal from '../BaseModal.vue';
import UiImageEditor from '../UiImageEditor.vue';
import InputComponent from '../Input.vue';
import ButtonComponent from '../Button.vue';
import VueElementLoading from 'vue-element-loading';

const props = defineProps(['isModalOpened', 'initialData']);
const emits = defineEmits(['handleSubmit', 'onCloseModal', 'handleNewUpload', 'onRemoveImg']);

const isModalOpened = toRef(props, 'isModalOpened');
const initialData = toRef(props, 'initialData');
const croppedImg = ref(null);
const thumbImg = ref(null);
const croppedSize = ref(null);
const issue = ref(null);
const isErr = ref(false);
const isLoading = ref(false);
const imgAltText = ref(
  [
    {
      key: "Alt-It",
      value: "",
      languageCode: "it"
    },
    {
      key: "Alt-En",
      value: "",
      languageCode: "en"
    },
  ]
);

const isEdit = computed(() => initialData.value?.card);

const onSubmit = () => {
  emits('handleSubmit', {
    card: croppedImg.value,
    thumb: thumbImg.value,
  });
  onCloseModal();
}

const onCloseModal = () => {
  emits('onCloseModal');
}

const handleImgCropped = (img, width, height) => {
  croppedImg.value = img;
  croppedSize.value = { width, height };

  if (width < 380 || height < 475) {
    issue.value = "L'immagine non ha le dimensioni minime necessarie. (380x475)";
    isErr.value = true;
  } else {
    issue.value = null;
    isErr.value = false;
  }

  // Create a thumbnail image with 40px by 40px
  const targetWidth = 40;
  const targetHeight = 40;
  const resizedCanvas = document.createElement('canvas');
  resizedCanvas.width = targetWidth;
  resizedCanvas.height = targetHeight;
  const ctx = resizedCanvas.getContext('2d');

  const image = new Image();
  image.src = img;
  image.onload = () => {
    ctx.drawImage(image, 0, 0, targetWidth, targetHeight);
    thumbImg.value = resizedCanvas.toDataURL();
  };
}

const onNewUpload = () => {
  emits('handleNewUpload', initialData.value?.uuid);
}

const onRemoveImg = () => {
  emits('onRemoveImg', initialData.value?.uuid);
}

const onLoaded = () => {
  isLoading.value = false;
}

const handleAltText = (text) => {
  if (text?.lang === 'it') {
    imgAltText.value[0].value = text.text;
  } else if (text?.lang === 'en') {
    imgAltText.value[1].value = text.text;
  }
}

onMounted(() => {
  isLoading.value = true;
});
</script>

<template>
  <base-modal :isOpen="isModalOpened" @modal-close="onCloseModal" @submit="onSubmit" name="edit-modal">
    <template #header>
      <h1 class="font-display-4">Modifica dell'immagine</h1>
    </template>
    <template #content>
      <div class="row p-4 bg-gray-light rounded">
        <div class="col-6">
          <ui-image-editor :img="initialData?.full" @imgCroppedChanged="handleImgCropped" @onLoaded="onLoaded" />
          <p class="text-center mb-5">
            Cropped Size: 
            <small class="text-muted">{{ croppedSize?.width }} * {{ croppedSize?.height }}</small>
          </p>
          <div class="alt-wrapper">
            <input-component title="Testo alternativo" :max-number="50" :text="imgAltText[0].value" placeholder="Inserisci il testo alternativo" language="it" @handleText="handleAltText" />
            <input-component title="Alt Text" :max-number="50" :text="imgAltText[1].value" placeholder="Enter Alt Text" language="en" @handleText="handleAltText" />
          </div>
        </div>
        <div class="col-6 d-flex gap-xs flex-column">
          <h3>Anteprime in app:</h3>
          <div v-if="isLoading" class="row h-100 mb-5 mt-5">
            <vue-element-loading :active="isLoading" spinner="spinner" color="#2D489D" />
          </div>
          <p v-else-if="isErr" class="text-danger">{{ issue }}</p>
          <div v-else>
            <div class="row mb-5">
              <div class="col-6 p-1 position-relative">
                <div class="crop-gallery">
                  <img :src="croppedImg" alt="cropped-img">
                </div>
                <p class="preview-img-label">Gallery</p>
              </div>
              <div class="col-6 p-1 position-relative">
                <div class="crop-cover">
                  <img :src="croppedImg" alt="cropped-img">
                </div>
                <p class="preview-img-label">Cover</p>
              </div>
            </div>
            <div class="row">
              <div class="col-6 p-1 position-relative">
                <div class="crop-lista">
                  <img :src="croppedImg" alt="cropped-img">
                </div>
                <p class="preview-img-label">Lista</p>
              </div>
              <div class="col-6 p-1 position-relative">
                <div class="crop-quadrata">
                  <img :src="croppedImg" alt="cropped-img">
                </div>
                <p class="preview-img-label">Quadrata</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="d-flex footer-buttons">
        <button-component text="Annulla" type="primary" class="bg-red" @handleClick="onCloseModal" />
        <ButtonComponent v-if="isEdit" text="Elimina" type="remove" class="bg-red border-red" :isDisable="isLoading" @handleClick="onRemoveImg" />
        <button-component text="Sostituisci" type="regular" class="bg-white" :isDisable="isLoading" @handleClick="onNewUpload" />
        <button-component text="Conferma" type="primary" class="bg-primary-custom" :isDisable="isErr || isLoading" @handleClick="onSubmit" />
      </div>
    </template>
  </base-modal>
</template>

<style scoped lang="scss">
.preview-img-label {
  font-size: 12px;
  color: var(--neutrali-grigio-scuro, #4C4C4C);
  text-align: center;
  position: absolute;
  margin-bottom: 0;
  bottom: -24px;
  left: 50%;
  transform: translateX(-50%);
}
.footer-buttons {
  gap: 8px;
  width: 100%;
  & > button:first-child {
    margin-left: auto;
  }
}
</style>